/* eslint-disable camelcase */
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { TimerWrapper, TimeCounter, TimerDescription } from './timer.styled';
import { Count, timeDiff, useEventTimer, useQuizTimer } from './timer-utils';
import { updateRespondentQuizTimerInfo } from '../../../form/reducer/form';
import { FormContext } from '../../../customise/contexts/form-context';

const Timer = ({
  timer,
  pauseTime,
  formDesign,
  timerDisplay,
  timerPurpose,
  isTimeElapsed,
  timerCountdownTo,
  timerDescription,
}) => {
  const timerComponents = [];
  const dispatch = useDispatch();
  const { inCustomise, inForm } = useContext(FormContext);
  const { inPreviewPage: inBuilderPreviewMode, previewViewPort } = useSelector((state) => state.builder?.present);

  const formWidth = formDesign?.design.custom_form_width;
  const inPreviewMobile = inBuilderPreviewMode && previewViewPort === 'mobile';

  const quizTimeleft = useQuizTimer(inForm, timer, pauseTime);
  const eventTimeLeft = useEventTimer(inForm, timerCountdownTo);

  const timeLeft = timerPurpose === 'event' ? eventTimeLeft : quizTimeleft;
  const { Days, Hours, Minutes, Seconds } = timeLeft;

  const timerIsDead =
    timerPurpose === 'event'
      ? Days === 0 && Hours === 0 && Minutes === 0 && Seconds === 0
      : Hours === 0 && Minutes === 0 && Seconds === 0;

  const timerIsAlmostDead =
    timerPurpose === 'event' ? Days === 0 && Hours === 0 && Minutes < 2 : Hours === 0 && Minutes < 2;

  Object.entries(timeLeft).forEach(([metric, value]) => {
    const time = value.toString().padStart(2, '0');
    timerComponents.push(
      <React.Fragment key={metric}>
        <Count time={time} metric={metric} />
        {metric !== 'Seconds' ? ':' : ''}
      </React.Fragment>
    );
  });

  // Keep record of respondent elapsed time on quizzes
  useEffect(() => {
    if (inForm && timerPurpose === 'quiz' && !pauseTime) {
      const startTime = `${timer.HH}:${timer.MM}:${timer.SS}`;
      const currentTime = `${timeLeft.Hours}:${timeLeft.Minutes}:${timeLeft.Seconds}`;
      const timeElapsed = timeDiff(startTime, currentTime);
      dispatch(updateRespondentQuizTimerInfo({ timeElapsed }));
    }
  }, [timeLeft]);

  // Notify form when timer is dead
  useEffect(() => {
    if (inForm && timerIsDead) {
      isTimeElapsed();
    }
  }, [inForm, timerIsDead]);

  return (
    <TimerWrapper
      style={
        inForm || inCustomise
          ? {
              '--font-family': formDesign.old.form_font,
            }
          : undefined
      }
      formWidth={formWidth}
      inForm={inForm || inCustomise}
      inPreviewMobile={inPreviewMobile}>
      <TimeCounter
        formWidth={formWidth}
        timerDisplay={timerDisplay}
        inForm={inForm || inCustomise}
        inPreviewMobile={inPreviewMobile}
        timerIsAlmostDead={timerIsAlmostDead}>
        <>{!timerIsDead ? timerComponents : <span>Time&apos;s up!</span>}</>
      </TimeCounter>
      {timerDescription && (
        <TimerDescription inForm={inForm || inCustomise} inPreviewMobile={inPreviewMobile}>
          {timerDescription}
        </TimerDescription>
      )}
    </TimerWrapper>
  );
};

Timer.defaultProps = {
  timer: {},
  pauseTime: false,
  timerCountdownTo: '',
  timerDescription: '',
  formDesign: undefined,
  timerDisplay: 'minimal',
  isTimeElapsed: () => null,
};

Timer.propTypes = {
  timer: PropTypes.shape({
    HH: PropTypes.number,
    MM: PropTypes.number,
    SS: PropTypes.number,
  }),
  pauseTime: PropTypes.bool,
  isTimeElapsed: PropTypes.func,
  timerDisplay: PropTypes.string,
  timerCountdownTo: PropTypes.string,
  timerDescription: PropTypes.string,
  formDesign: PropTypes.shape({
    old: PropTypes.shape({
      form_font: PropTypes.string,
    }),
    design: PropTypes.shape({
      custom_form_width: PropTypes.number,
    }),
  }),
  timerPurpose: PropTypes.string.isRequired,
};

export default Timer;
